import { type AppPermissionIdentifierWithEffect } from "./types";

// value should match cognito group names
export enum ValidCognitoPermissionGroupsEnum {
  Admin = "Admin",
  Manager = "Manager",
  Supervisor = "Supervisor",
  Operator = "Operator",
  Guest = "Guest",
}

export const AppPermissionResource = {
  Admin: [
    // view and update cells in /admin/cell
    "UpdateCell",
    // view and update plant in /admin/plant
    "UpdatePlant",
    // invite users and view /GenerateSignUpCode
    "InviteUser",
    // view and update tenant permissions in /admin/TenantPermission
    "UpdateTenantPermission",
    // view and update user's own profile in /settings/user
    "UpdateSelfProfile",
    // view and update users in /admin/user
    "UpdateUser",
    // view and update notifications in /admin/notification
    "UpdateNotification",
    // create/update/delete NCR reports in /admin/ncr
    "ManageNCR",
    // create/update/delete machinery and log machine checks in /admin/machinery
    "ManageMachinery",
  ],
  CallHelp: [
    // feature flag for cell help stacklight/call help
    "ViewFeature",
    // update cell help stacklight state
    "UpdateState",
  ],
  IOT: [
    // view /IOTOverview
    "ViewIOTOverview",
    // update device maintenance status
    "UpdateMaintenanceStatus",
    // send reboot command to an IoT device
    "ResetDevice",
  ],
  Inventory: [
    // view /inventory
    "ViewInventory",
    // create/update/delete inventory items
    "ManageInventory",
    // download inventory xls file from /inventory
    "ExportInventory",
  ],
  Machinery: [
    // feature flag to view /machinery/inspection
    "ViewFeature",
    // ability to log a machinery check (sign off)
    "SignMachine",
  ],
  Kanban: [
    // feature flag for RFID kanban
    "ViewFeature",
    // view /kanban
    "ViewKanbanOverview",
    "ViewKanbanStock",
    // create/update kanban items
    "UpdateKanbanDetails",
    // manually adjust kanban stock
    "UpdateKanbanStock",
    // delete kanban items
    "DeleteKanbanDetails",
    // update kanban order
    "UpdateKanbanOrder",
  ],
  MachineMonitoring: [
    // feature flag for machine monitoring
    "ViewFeature",
  ],
  PulseMeter: [
    // feature flag for pulse meter
    "ViewFeature",
    // view Hydro baseline settings, samples, and active-time threshold
    "ReadHydroBaseline",
  ],
  ProductionJob: [
    // view /JobOverview
    "ViewJobOverview",
    // view /ProjectOverview
    "ViewProjectDashboard",
    // view /production-storage and /production-storage-lookup
    "ViewJobStorage",
    // update job (access Update Job modal)
    "UpdateJob",
    // clock workers in and out of work orders
    "ClockWork",
    // create storage records
    "CreateJobStorage",
    // update storage records
    "UpdateJobStorage",
    // remove storage records
    "RemoveJobStorage",
    // delete storage records
    "DeleteJobStorage",
    // Search for Tag
    "SearchTag",
  ],
  RFID: [
    // feature flag for RFID
    "ViewFeature",
    // view / and /DeviceDetails, view tag history
    "ViewRFIDOverview",
    // change tag location
    "UpdateTagLocation",
    // download xls file from /
    "ExportTagLocation",
  ],
  GHG: [
    // view GHG (greenhouse gas) overview and data
    "ViewGHG",
    // create/update GHG entries
    "ManageGHGEntries",
  ],
  Help: [
    // create/update help content for each feature area
    "SmartCell",
    "RFID",
    "ProductionJob",
    "Project",
    "IOT",
    "Kanban",
    "WeldingParams",
    "NCR",
    "Inventory",
    "Machinery",
    "Maintenance",
    "GHG",
    "Home",
    "Power",
    "Software",
    "Admin",
    "Account",
  ],
  SmartCell: [
    // view /smartcell
    "ViewSmartCellOverview",
    // view /smartcell/$id
    "ViewSmartCellDetails",
  ],
  WeldingParams: [
    // feature flag for welding params
    "ViewWeldingParams",
    // create/update welding params
    "UpdateWeldingParams",
    // delete welding params
    "DeleteWeldingParams",
  ],
  // as const is needed for intellisense
} as const satisfies Record<string, Array<string>>;
export type AppPermissionResource = typeof AppPermissionResource;

export const AppPermissionResourceNameMap = {
  Admin: {
    name: "Admin",
    UpdateCell: "Manage Cells",
    UpdatePlant: "Manage Plants",
    UpdateSelfProfile: "Manage User's Own Profile",
    UpdateUser: "Manage All Users",
    InviteUser: "Invite Users",
    UpdateTenantPermission: "Manage Tenant Permissions",
    UpdateNotification: "Manage App Notifications",
    ManageNCR: "Manage Non-Conformance Reports",
    ManageMachinery: "Manage Machinery",
  },
  CallHelp: {
    name: "Call Help/Help Stacklight",
    ViewFeature: "Enable Viewing Stacklight States",
    UpdateState: "Update State",
  },
  IOT: {
    name: "IOT",
    ViewIOTOverview: "View IOT Overview",
    UpdateMaintenanceStatus: "Update Device Maintenance Status",
    ResetDevice: "Reset IoT Device",
  },
  Inventory: {
    name: "Inventory",
    ViewInventory: "View Inventory",
    ManageInventory: "Create/Update/Delete Inventory Items",
    ExportInventory: "Download Inventory Report",
  },
  Machinery: {
    name: "Machinery",
    ViewFeature: "Enable Machinery Inspection Feature",
    SignMachine: "Sign Machine Checks",
  },
  Kanban: {
    name: "Kanban",
    ViewFeature: "Enable Kanban Feature",
    UpdateKanbanDetails: "Create/Update Kanban Items",
    UpdateKanbanStock: "Update Kanban Stock",
    DeleteKanbanDetails: "Delete Kanban Items",
    UpdateKanbanOrder: "Update Kanban Order",
    ViewKanbanOverview: "View Kanban Overview",
    ViewKanbanStock: "View Kanban Stock Levels",
  },
  MachineMonitoring: {
    name: "Machine Monitoring",
    ViewFeature: "Enable Machine Monitoring Feature",
  },
  PulseMeter: {
    name: "Pulse Meter",
    ViewFeature: "Enable Pulse Meter Feature",
    ReadHydroBaseline: "View Hydro Baseline and Active Time",
  },
  ProductionJob: {
    name: "Production Job",
    UpdateJob: "Update Job",
    ClockWork: "Clock in and out of work orders",
    ViewJobOverview: "View Job Overview",
    ViewJobStorage: "View Job Storage",
    CreateJobStorage: "Create Job Storage",
    UpdateJobStorage: "Update Job Storage",
    RemoveJobStorage: "Remove Job Storage",
    DeleteJobStorage: "Delete Job Storage",
    ViewProjectDashboard: "View Project Dashboard",
    SearchTag: "Search Tag",
  },
  RFID: {
    name: "RFID",
    UpdateTagLocation: "Update Tag Location",
    ViewFeature: "Enable RFID Feature",
    ViewRFIDOverview: "View RFID Overview",
    ExportTagLocation: "Download Tag Location Report",
  },
  SmartCell: {
    name: "Smart Cell",
    ViewSmartCellOverview: "View Smart Cell Overview Page",
    ViewSmartCellDetails: "View Smart Cell Details Page",
  },
  WeldingParams: {
    name: "Welding Params",
    ViewWeldingParams: "View Welding Params",
    UpdateWeldingParams: "Create/Update Welding Params",
    DeleteWeldingParams: "Delete Welding Params",
  },
  GHG: {
    name: "Greenhouse Gas",
    ViewGHG: "View GHG Emissions Page",
    ManageGHGEntries: "Create GHG Data Entries",
  },
  Help: {
    name: "Help",
    SmartCell: "Manage Smart Cell Help",
    RFID: "Manage RFID Help",
    ProductionJob: "Manage Production Job Help",
    Project: "Manage Project Help",
    IOT: "Manage IOT Help",
    Kanban: "Manage Kanban Help",
    WeldingParams: "Manage Welding Params Help",
    NCR: "Manage Non-Conformance Report Help",
    Inventory: "Manage Inventory Help",
    Machinery: "Manage Machinery Help",
    Maintenance: "Manage Maintenance Department Help",
    GHG: "Manage Greenhouse Gas Help",
    Home: "Manage Home Help",
    Power: "Manage Power Help",
    Software: "Manage Software Help",
    Admin: "Manage Admin Help",
    Account: "Manage Account Help",
  },
} satisfies {
  [P in keyof AppPermissionResource]: {
    [K in AppPermissionResource[P][number]]: string;
  } & {
    name: string;
  };
};

export const CognitoUserGroupPermissionDefaults = {
  [ValidCognitoPermissionGroupsEnum.Admin]: [
    "*::*::allow",
    "Admin::UpdateTenantPermission::deny",
    "Admin::UpdateUser::deny",
  ],
  [ValidCognitoPermissionGroupsEnum.Manager]: [
    "Admin::UpdateSelfProfile::allow",
    "Admin::InviteUser::allow",
    "CallHelp::*::allow",
    "GHG::ViewGHG::allow",
    "Help::*::allow",
    "IOT::*::allow",
    "Inventory::*::allow",
    "Kanban::*::allow",
    "Machinery::*::allow",
    "MachineMonitoring::*::allow",
    "PulseMeter::ViewFeature::allow",
    "ProductionJob::*::allow",
    "RFID::*::allow",
    "SmartCell::*::allow",
    "WeldingParams::*::allow",
  ],
  [ValidCognitoPermissionGroupsEnum.Supervisor]: [
    "Admin::UpdateSelfProfile::allow",
    "CallHelp::*::allow",
    "GHG::ViewGHG::allow",
    "IOT::*::allow",
    "Inventory::*::allow",
    "Kanban::*::allow",
    "Machinery::*::allow",
    "MachineMonitoring::View*::allow",
    "PulseMeter::View*::allow",
    "ProductionJob::*::allow",
    "RFID::*::allow",
    "SmartCell::*::allow",
    "WeldingParams::*::allow",
  ],
  [ValidCognitoPermissionGroupsEnum.Operator]: [
    "Admin::UpdateSelfProfile::allow",
    "CallHelp::*::allow",
    "Machinery::ViewFeature::allow",
    "Machinery::SignMachine::allow",
    "Inventory::ViewInventory::allow",
    "Inventory::ExportInventory::allow",
    "MachineMonitoring::View*::allow",
    "PulseMeter::View*::allow",
    "ProductionJob::ClockWork::allow",
    "RFID::View*::allow",
    "SmartCell::View*::allow",
    "WeldingParams::*::allow",
  ],
  [ValidCognitoPermissionGroupsEnum.Guest]: ["*::*::deny"],
} satisfies {
  [P in ValidCognitoPermissionGroupsEnum]: AppPermissionIdentifierWithEffect[];
};
